/**
 * Hotel Amenities Allocation Dialog Component
 * Allows Company Admin to allocate global amenities and amenity groups to hotels
 */
import { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { 
  Layers, FolderPlus, Loader2, CheckCircle, XCircle, 
  AlertCircle, Image
} from 'lucide-react';

export default function HotelAmenitiesAllocationDialog({ 
  open, 
  onOpenChange, 
  hotelId, 
  hotelName 
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [amenities, setAmenities] = useState([]);
  const [groups, setGroups] = useState([]);
  const [hotelAmenities, setHotelAmenities] = useState([]);
  const [hotelGroups, setHotelGroups] = useState([]);
  
  useEffect(() => {
    if (open && hotelId) {
      loadData();
    }
  }, [open, hotelId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [amenitiesRes, groupsRes, hotelAmenitiesRes] = await Promise.all([
        api.get('/amenity-management'),
        api.get('/amenity-management/groups'),
        api.get(`/amenity-management/hotels/${hotelId}`).catch(() => ({ data: [] }))
      ]);
      
      setAmenities(amenitiesRes.data || []);
      setGroups(groupsRes.data || []);
      
      // Map hotel amenities by ID for quick lookup
      const activeAmenityIds = (hotelAmenitiesRes.data || [])
        .filter(a => a.is_active)
        .map(a => a.id);
      setHotelAmenities(activeAmenityIds);
      
      // Determine active groups (all amenities in a group are active)
      const groupAmenityMap = {};
      (amenitiesRes.data || []).forEach(a => {
        if (!groupAmenityMap[a.group_id]) {
          groupAmenityMap[a.group_id] = [];
        }
        groupAmenityMap[a.group_id].push(a.id);
      });
      
      const activeGroups = Object.keys(groupAmenityMap).filter(groupId => {
        const groupAmenityIds = groupAmenityMap[groupId];
        return groupAmenityIds.every(id => activeAmenityIds.includes(id));
      });
      setHotelGroups(activeGroups);
      
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load amenities data');
    } finally {
      setLoading(false);
    }
  };

  const toggleAmenity = async (amenityId) => {
    const isCurrentlyActive = hotelAmenities.includes(amenityId);
    setSaving(true);
    
    try {
      await api.put(`/amenity-management/hotels/${hotelId}`, {
        amenity_ids: [amenityId],
        is_active: !isCurrentlyActive
      });
      
      if (isCurrentlyActive) {
        setHotelAmenities(prev => prev.filter(id => id !== amenityId));
      } else {
        setHotelAmenities(prev => [...prev, amenityId]);
      }
      
      toast.success(`Amenity ${!isCurrentlyActive ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error('Failed to update amenity');
    } finally {
      setSaving(false);
    }
  };

  const toggleGroup = async (groupId) => {
    const groupAmenityIds = amenities
      .filter(a => a.group_id === groupId)
      .map(a => a.id);
    
    if (groupAmenityIds.length === 0) {
      toast.error('No amenities in this group');
      return;
    }
    
    const isGroupActive = hotelGroups.includes(groupId);
    setSaving(true);
    
    try {
      await api.put(`/amenity-management/hotels/${hotelId}`, {
        amenity_ids: groupAmenityIds,
        is_active: !isGroupActive
      });
      
      if (isGroupActive) {
        setHotelAmenities(prev => prev.filter(id => !groupAmenityIds.includes(id)));
        setHotelGroups(prev => prev.filter(id => id !== groupId));
      } else {
        setHotelAmenities(prev => [...new Set([...prev, ...groupAmenityIds])]);
        setHotelGroups(prev => [...prev, groupId]);
      }
      
      toast.success(`All amenities in group ${!isGroupActive ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error('Failed to update group amenities');
    } finally {
      setSaving(false);
    }
  };

  const activateAll = async () => {
    const allAmenityIds = amenities.map(a => a.id);
    setSaving(true);
    
    try {
      await api.put(`/amenity-management/hotels/${hotelId}`, {
        amenity_ids: allAmenityIds,
        is_active: true
      });
      
      setHotelAmenities(allAmenityIds);
      setHotelGroups(groups.map(g => g.id));
      toast.success('All amenities activated');
    } catch (error) {
      toast.error('Failed to activate amenities');
    } finally {
      setSaving(false);
    }
  };

  const deactivateAll = async () => {
    const allAmenityIds = amenities.map(a => a.id);
    setSaving(true);
    
    try {
      await api.put(`/amenity-management/hotels/${hotelId}`, {
        amenity_ids: allAmenityIds,
        is_active: false
      });
      
      setHotelAmenities([]);
      setHotelGroups([]);
      toast.success('All amenities deactivated');
    } catch (error) {
      toast.error('Failed to deactivate amenities');
    } finally {
      setSaving(false);
    }
  };

  const getAmenitiesByGroup = (groupId) => {
    return amenities.filter(a => a.group_id === groupId);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-indigo-600" />
            Amenities Allocation - {hotelName}
          </DialogTitle>
          <DialogDescription>
            Allocate amenities and amenity groups to this hotel. Hotel Admin can then activate these at room type level.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
          </div>
        ) : (
          <Tabs defaultValue="groups" className="w-full">
            <div className="flex items-center justify-between mb-4">
              <TabsList>
                <TabsTrigger value="groups" className="flex items-center gap-2">
                  <FolderPlus className="h-4 w-4" />
                  By Groups
                </TabsTrigger>
                <TabsTrigger value="amenities" className="flex items-center gap-2">
                  <Layers className="h-4 w-4" />
                  All Amenities
                </TabsTrigger>
              </TabsList>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={activateAll}
                  disabled={saving}
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Activate All
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={deactivateAll}
                  disabled={saving}
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Deactivate All
                </Button>
              </div>
            </div>

            {/* By Groups Tab */}
            <TabsContent value="groups">
              <ScrollArea className="h-[50vh]">
                {groups.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center">
                      <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-500">No amenity groups available</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {groups.map(group => {
                      const groupAmenities = getAmenitiesByGroup(group.id);
                      const activeCount = groupAmenities.filter(a => hotelAmenities.includes(a.id)).length;
                      const isGroupActive = hotelGroups.includes(group.id);
                      
                      return (
                        <Card key={group.id}>
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <FolderPlus className="h-5 w-5 text-indigo-500" />
                                <div>
                                  <h4 className="font-medium">{group.name}</h4>
                                  <p className="text-sm text-gray-500">
                                    {activeCount} / {groupAmenities.length} amenities active
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <Badge variant={group.level === 'hotel' ? 'default' : 'secondary'}>
                                  {group.level}
                                </Badge>
                                <Switch
                                  checked={isGroupActive}
                                  onCheckedChange={() => toggleGroup(group.id)}
                                  disabled={saving || groupAmenities.length === 0}
                                />
                              </div>
                            </div>
                            
                            {groupAmenities.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-2">
                                {groupAmenities.map(amenity => (
                                  <Badge 
                                    key={amenity.id}
                                    variant={hotelAmenities.includes(amenity.id) ? 'default' : 'outline'}
                                    className="cursor-pointer"
                                    onClick={() => toggleAmenity(amenity.id)}
                                  >
                                    {hotelAmenities.includes(amenity.id) && (
                                      <CheckCircle className="h-3 w-3 mr-1" />
                                    )}
                                    {amenity.name}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            {/* All Amenities Tab */}
            <TabsContent value="amenities">
              <ScrollArea className="h-[50vh]">
                {amenities.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center">
                      <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-500">No amenities available</p>
                      <p className="text-sm text-gray-400 mt-1">
                        Super Admin needs to create amenities first
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {amenities.map(amenity => {
                      const isActive = hotelAmenities.includes(amenity.id);
                      
                      return (
                        <Card 
                          key={amenity.id}
                          className={`cursor-pointer transition-all ${isActive ? 'border-green-500 bg-green-50' : 'hover:border-gray-300'}`}
                          onClick={() => toggleAmenity(amenity.id)}
                        >
                          <CardContent className="p-3 flex items-center gap-3">
                            {amenity.icon_url ? (
                              <img 
                                src={amenity.icon_url} 
                                alt={amenity.name}
                                className="h-8 w-8 rounded object-cover"
                              />
                            ) : (
                              <div className="h-8 w-8 rounded bg-gray-100 flex items-center justify-center">
                                <Image className="h-4 w-4 text-gray-400" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{amenity.name}</p>
                              <p className="text-xs text-gray-500">{amenity.group_name}</p>
                            </div>
                            {isActive && (
                              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          </Tabs>
        )}

        <div className="flex justify-between items-center pt-4 border-t">
          <p className="text-sm text-gray-500">
            {hotelAmenities.length} amenities allocated to this hotel
          </p>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
